// The inline text editor of the localization grids (spec/dashboard.md section 9.1): commits on Enter and on blur, Escape
// cancels; multi-line mode keeps Shift+Enter for a line break. One commit is one coalesced change (Q-D-7).
import { useEffect, useRef, useState } from 'react';

export interface CellTextEditorProps {
  value: string;
  onCommit: (value: string) => void;
  onCancel?: () => void;
  multiline?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
  /** an error the owner shows on the input */
  invalid?: boolean;
}

export function CellTextEditor({ value, onCommit, onCancel, multiline, placeholder, autoFocus, disabled, className = '', invalid, 'aria-label': ariaLabel }: CellTextEditorProps) {
  const [text, setText] = useState(value);
  const done = useRef(false);
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  const commit = () => {
    if (done.current) return;
    done.current = true;
    if (text !== value) onCommit(text);
    else onCancel?.();
    // the editor may stay mounted (a new row's always-open key cell): accept the next edit
    setTimeout(() => (done.current = false), 0);
  };
  const cancel = () => {
    if (done.current) return;
    done.current = true;
    setText(value);
    onCancel?.();
    setTimeout(() => (done.current = false), 0);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !(multiline && e.shiftKey)) {
      e.preventDefault();
      e.stopPropagation();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    }
  };
  const common = {
    value: text,
    placeholder,
    disabled,
    'aria-label': ariaLabel,
    'aria-invalid': invalid || undefined,
    onBlur: commit,
    onKeyDown,
  };
  return multiline ? (
    <textarea ref={ref} className={`textarea loc-cell-editor ${className}`} rows={Math.min(6, Math.max(2, text.split('\n').length))} onChange={(e) => setText(e.target.value)} {...common} />
  ) : (
    <input ref={ref} type="text" className={`input input-s loc-cell-editor ${className}`} onChange={(e) => setText(e.target.value)} {...common} />
  );
}
